import { createContext, useContext, useEffect, useState, useCallback, PropsWithChildren, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Collapse, IconButton } from '@mui/material';
import { VolumeUp, Close } from '@mui/icons-material';

import { UserRoles } from '@bandido/shared/types';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';

interface CallData {
  phoneNumber?: string;
  callSid?: string;
}

interface NotificationContextProps {
  permission: NotificationPermission;
  hasPermission: boolean;
  showCallNotification: (data: CallData) => void;
  audioContext: AudioContext | null;
  hasAudioPermission: boolean;
  resumeAudioContext: () => Promise<boolean>;
  setPendingRingtone: (pending: boolean) => void;
}

const NotificationContext = createContext<NotificationContextProps>({} as NotificationContextProps);

export const NotificationProvider = ({ children }: PropsWithChildren) => {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [showAudioBanner, setShowAudioBanner] = useState(false);
  const [hasAudioPermission, setHasAudioPermission] = useState(false);
  const [pendingRingtone, setPendingRingtone] = useState(false);
  const { t } = useTranslation();
  const { myProfile } = useAuth();
  const audioContextRef = useRef<AudioContext | null>(null);
  const hasInteractedRef = useRef(false);

  useEffect(() => {
    if (!audioContextRef.current && typeof window !== 'undefined' && window.AudioContext) {
      audioContextRef.current = new AudioContext();

      if (audioContextRef.current.state === 'running') {
        setHasAudioPermission(true);
        hasInteractedRef.current = true;
        console.log('AudioContext ya está en estado running');
      }
    }
  }, []);

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  useEffect(() => {
    if (myProfile) {
      const roles = myProfile.roles?.split(',') || [];
      const isAgent = roles.some((r) => [UserRoles.AGENTS].includes(r as UserRoles));

      if (isAgent) {
        const checkAudioContextState = () => {
          if (audioContextRef.current && audioContextRef.current.state === 'running' && !hasAudioPermission) {
            console.log('AudioContext detectado como running externamente');
            setHasAudioPermission(true);
            hasInteractedRef.current = true;
            setShowAudioBanner(false);
          }
        };

        checkAudioContextState();

        const intervalId = setInterval(checkAudioContextState, 500);
        const timeoutId = setTimeout(() => clearInterval(intervalId), 5000);

        if (!hasAudioPermission && !hasInteractedRef.current) {
          setTimeout(() => {
            if (!hasAudioPermission && !hasInteractedRef.current) {
              setShowAudioBanner(true);
            }
          }, 2000);
        }

        if (permission === 'default') {
          requestPermission();
        }

        return () => {
          clearInterval(intervalId);
          clearTimeout(timeoutId);
        };
      }
    }
  }, [myProfile, permission, hasAudioPermission]);

  const requestPermission = async () => {
    if (!('Notification' in window)) return false;
    if (Notification.permission === 'default') {
      const res = await Notification.requestPermission();
      setPermission(res);
      return res === 'granted';
    }
    return Notification.permission === 'granted';
  };

  const showCallNotification = useCallback((data: CallData) => {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    if (!document.hidden) return;

    const notificationInstance = new Notification(t('call.incoming.title'), {
      body: t('call.incoming.callFrom', { phoneNumber: data.phoneNumber ?? '' }),
      tag: 'incoming-call',
      icon: '/favicon.ico',
      // TODO: add requireInteraction
      // requireInteraction: true,
    });

    notificationInstance.onclick = () => {
      window.focus();
      notificationInstance.close();
    };
  }, []);

  const resumeAudioContext = useCallback(async () => {
    if (!audioContextRef.current) {
      console.log('AudioContext no está disponible');
      return false;
    }

    const currentState = audioContextRef.current.state;
    console.log('Estado actual del AudioContext:', currentState);

    if (currentState === 'running') {
      setHasAudioPermission(true);
      hasInteractedRef.current = true;
      setShowAudioBanner(false);
      console.log('AudioContext ya está running');
      return true;
    }

    if (currentState === 'suspended') {
      try {
        await audioContextRef.current.resume();

        if (audioContextRef.current.state === 'running') {
          setHasAudioPermission(true);
          hasInteractedRef.current = true;
          setShowAudioBanner(false);
          console.log('AudioContext resumido exitosamente');
          return true;
        } else {
          console.log('AudioContext no se pudo resumir completamente');
          return false;
        }
      } catch (error) {
        console.error('Error al resumir AudioContext:', error);
        return false;
      }
    }

    if (currentState === 'closed') {
      try {
        audioContextRef.current = new AudioContext();
        if (audioContextRef.current.state === 'running') {
          setHasAudioPermission(true);
          hasInteractedRef.current = true;
          setShowAudioBanner(false);
          console.log('Nuevo AudioContext creado y running');
          return true;
        }
      } catch (error) {
        console.error('Error al crear nuevo AudioContext:', error);
        return false;
      }
    }

    return false;
  }, []);

  const unlockAudio = useCallback(async () => {
    if (!audioContextRef.current) return false;

    try {
      const buffer = audioContextRef.current.createBuffer(1, 1, 22050);
      const source = audioContextRef.current.createBufferSource();
      source.buffer = buffer;

      const gainNode = audioContextRef.current.createGain();
      gainNode.gain.value = 0.001;

      source.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);

      source.start();

      console.log('Audio desbloqueado con sonido silencioso');
      return true;
    } catch (error) {
      console.error('Error desbloqueando audio:', error);
      return false;
    }
  }, []);

  const handleEnableAudio = useCallback(async () => {
    setShowAudioBanner(false);
    hasInteractedRef.current = true;

    const success = await resumeAudioContext();

    if (success) {
      if (pendingRingtone) {
        setPendingRingtone(false);
      }

      await unlockAudio();
    }
  }, [resumeAudioContext, pendingRingtone, unlockAudio]);

  const handleDismissBanner = () => {
    setShowAudioBanner(false);
  };

  useEffect(() => {
    if (!hasAudioPermission && myProfile) {
      const roles = myProfile.roles?.split(',') || [];
      const isAgent = roles.some((r) => [UserRoles.AGENTS].includes(r as UserRoles));

      if (isAgent) {
        const handleFirstInteraction = async (event: Event) => {
          console.log('Primera interacción detectada:', event.type);
          hasInteractedRef.current = true;

          const success = await resumeAudioContext();

          if (success) {
            console.log('Audio habilitado automáticamente con la primera interacción');
            setShowAudioBanner(false);

            await unlockAudio();

            if (pendingRingtone) {
              setPendingRingtone(false);
            }

            events.forEach(eventType => {
              document.removeEventListener(eventType, handleFirstInteraction, { capture: true });
              window.removeEventListener(eventType, handleFirstInteraction, { capture: true });
            });
          } else {
            console.log('No se pudo habilitar audio automáticamente, mostrando banner');
            if (!showAudioBanner) {
              setShowAudioBanner(true);
            }
          }
        };

        const events = ['click', 'touchstart', 'keydown', 'mousedown', 'pointerdown'];

        const addListeners = () => {
          events.forEach(event => {
            document.addEventListener(event, handleFirstInteraction, { once: false, capture: true, passive: true });
            window.addEventListener(event, handleFirstInteraction, { once: false, capture: true, passive: true });
          });
        };

        const timeoutId = setTimeout(addListeners, 100);

        return () => {
          clearTimeout(timeoutId);
          events.forEach(event => {
            document.removeEventListener(event, handleFirstInteraction, { capture: true });
            window.removeEventListener(event, handleFirstInteraction, { capture: true });
          });
        };
      }
    }
  }, [hasAudioPermission, myProfile, resumeAudioContext, pendingRingtone, showAudioBanner, unlockAudio]);

  return (
    <NotificationContext.Provider
      value={{
        permission,
        hasPermission: permission === 'granted',
        showCallNotification,
        audioContext: audioContextRef.current,
        hasAudioPermission,
        resumeAudioContext,
        setPendingRingtone,
      }}
    >
      {children}

      <Collapse
        in={showAudioBanner}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1400,
        }}
      >
        <Alert
          severity="info"
          icon={<VolumeUp />}
          action={
            <>
              <Button
                color="inherit"
                size="small"
                onClick={handleEnableAudio}
                sx={{ fontWeight: 'bold' }}
              >
                {t('calls.interaction.enable_audio')}
              </Button>
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={handleDismissBanner}
              >
                <Close fontSize="inherit" />
              </IconButton>
            </>
          }
          sx={{
            m: 0,
            borderRadius: 0,
            '& .MuiAlert-message': {
              flex: 1,
            },
          }}
        >
          {t('calls.interaction.banner_message')}
        </Alert>
      </Collapse>
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
